import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResponsiveContainer } from "@/components/ui/responsive-container";
import { ResponsiveFormGroup } from "@/components/ui/responsive-form";
import { IntakeData } from "../../pages/Index";
import { Heart, Plane, Calendar } from "lucide-react";

interface MedicalIntakeStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
}

export default function MedicalIntakeStep({
  data,
  onUpdate,
  onNext,
}: MedicalIntakeStepProps) {
  const [formData, setFormData] = useState({
    dateOfBirth: data.dateOfBirth,
    city: data.city,
    state: data.state,
    country: data.country,
    preExistingConditions: data.preExistingConditions,
    currentMedications: data.currentMedications,
    allergies: data.allergies,
    majorSurgeries: data.majorSurgeries,
    dentalProcedureType: data.dentalProcedureType,
    dentalDiscomfort: data.dentalDiscomfort,
    mobilityIssues: data.mobilityIssues,
    dietaryRestrictions: data.dietaryRestrictions,
    travelConcerns: data.travelConcerns,
    smokingHabits: data.smokingHabits,
    travelAlone: data.travelAlone,
    budgetExpectations: data.budgetExpectations,
    personalInterest: data.personalInterest,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);

  // Remove forced scroll on mount to prevent jump-to-top on visit
  useEffect(() => {}, []);

  // Today string for date input max (prevents selecting future dates)
  const todayStr = new Date().toISOString().split("T")[0];

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Removed unused handleKeyPress function

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else {
      // Validate age range (1-150 years)
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Adjust age if birthday hasn't occurred this year
      const actualAge =
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ? age - 1
          : age;

      if (birthDate > today) {
        newErrors.dateOfBirth = "Date of birth cannot be in the future";
      } else if (actualAge < 1) {
        newErrors.dateOfBirth = "Age must be at least 1 year old";
      } else if (actualAge > 150) {
        newErrors.dateOfBirth = "Age cannot exceed 150 years";
      }
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }
    if (!formData.smokingHabits.trim()) {
      newErrors.smokingHabits = "Please select your smoking habits";
    }
    if (!formData.dentalProcedureType) {
      newErrors.dentalProcedureType = "Please select a dental procedure type";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleContinue = () => {
    const validationErrors = validateForm();
    const isValid = Object.keys(validationErrors).length === 0;
    if (isValid) {
      onUpdate(formData);
      onNext();
    } else {
      // Scroll to first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => element.focus(), 500);
      }
    }
  };

  return (
    <div className="h-full">
      {/* Mobile: Complete form layout */}
      <div className="lg:hidden space-y-6 bg-white py-6">
        <ResponsiveContainer variant="standard">
          {/* Header */}
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Medical & Travel Information
            </h2>
            <p className="text-sm text-gray-600">
              Help us prepare the best treatment plan for you
            </p>
            <br />
          </div>

          <form
            ref={formRef}
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleContinue();
            }}
          >
            {/* Basic Information Section */}
            <div className="border border-gray-200 rounded-xl p-4 sm:p-6 space-y-4 bg-white shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
                <Calendar className="w-4 h-4" />
                Basic Information
              </h3>

              <div>
                <Label
                  htmlFor="dateOfBirth"
                  className="text-sm font-medium text-gray-700"
                >
                  Date of Birth *
                </Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) =>
                    handleInputChange("dateOfBirth", e.target.value)
                  }
                  max={todayStr}
                  className={`mt-1 h-12 text-base ${errors.dateOfBirth ? "border-red-500" : ""}`}
                />
                {errors.dateOfBirth && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>

              <ResponsiveFormGroup columns={2}>
                <div>
                  <Label
                    htmlFor="city"
                    className="text-sm font-medium text-gray-700"
                  >
                    City *
                  </Label>
                  <Input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="Your city"
                    className={`mt-1 h-12 text-base ${errors.city ? "border-red-500" : ""}`}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="state"
                    className="text-sm font-medium text-gray-700"
                  >
                    State/Province
                  </Label>
                  <Input
                    id="state"
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    placeholder="State/Province"
                    className="mt-1 h-12 text-base"
                  />
                </div>
              </ResponsiveFormGroup>

              <div>
                <Label
                  htmlFor="country"
                  className="text-sm font-medium text-gray-700"
                >
                  Country *
                </Label>
                <Input
                  id="country"
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  placeholder="Your country"
                  className={`mt-1 h-12 text-base ${errors.country ? "border-red-500" : ""}`}
                />
                {errors.country && (
                  <p className="text-red-500 text-xs mt-1">{errors.country}</p>
                )}
              </div>
            </div>

            {/* Medical History Section */}
            <div className="border border-gray-200 rounded-xl p-4 sm:p-6 space-y-4 bg-white shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
                <Heart className="w-4 h-4" />
                Medical History
              </h3>

              <div>
                <Label
                  htmlFor="preExisting"
                  className="text-sm font-medium text-gray-700"
                >
                  Do you have any pre-existing medical conditions?
                </Label>
                <Textarea
                  id="preExisting"
                  value={formData.preExistingConditions}
                  onChange={(e) =>
                    handleInputChange("preExistingConditions", e.target.value)
                  }
                  placeholder="List any medical conditions or write 'None'"
                  className="mt-1 min-h-[100px] text-base"
                />
              </div>

              <div>
                <Label
                  htmlFor="medications"
                  className="text-sm font-medium text-gray-700"
                >
                  Are you currently taking any medications?
                </Label>
                <Textarea
                  id="medications"
                  value={formData.currentMedications}
                  onChange={(e) =>
                    handleInputChange("currentMedications", e.target.value)
                  }
                  placeholder="List medications and dosages or write 'None'"
                  className="mt-1 min-h-[100px] text-base"
                />
              </div>

              <div>
                <Label
                  htmlFor="allergies"
                  className="text-sm font-medium text-gray-700"
                >
                  Do you have any known allergies?
                </Label>
                <Textarea
                  id="allergies"
                  value={formData.allergies}
                  onChange={(e) =>
                    handleInputChange("allergies", e.target.value)
                  }
                  placeholder="List any allergies or write 'None'"
                  className="mt-1 min-h-[100px] text-base"
                />
              </div>

              <div>
                <Label
                  htmlFor="surgeries"
                  className="text-sm font-medium text-gray-700"
                >
                  Major surgeries or medical procedures?
                </Label>
                <Textarea
                  id="surgeries"
                  value={formData.majorSurgeries}
                  onChange={(e) =>
                    handleInputChange("majorSurgeries", e.target.value)
                  }
                  placeholder="List any major surgeries or procedures or write 'None'"
                  className="mt-1 min-h-[100px] text-base"
                />
              </div>
            </div>

            {/* Dental Information Section */}
            <div className="border border-gray-200 rounded-xl p-4 sm:p-6 space-y-4 bg-white shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
                <span className="text-blue-600">🦷</span>
                Dental Information
              </h3>

              <div>
                <Label
                  htmlFor="procedureType"
                  className="text-sm font-medium text-gray-700"
                >
                  What type of dental procedure are you seeking? *
                </Label>
                <Select
                  value={formData.dentalProcedureType}
                  onValueChange={(value) =>
                    handleInputChange("dentalProcedureType", value)
                  }
                >
                  <SelectTrigger
                    className={`mt-1 h-12 text-base ${errors.dentalProcedureType ? "border-red-500" : ""}`}
                  >
                    <SelectValue placeholder="Select procedure type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="implants">Dental Implants </SelectItem>
                    <SelectItem value="veneers">Veneers</SelectItem>
                    <SelectItem value="crowns">Crowns</SelectItem>
                    <SelectItem value="whitening">Teeth Whitening</SelectItem>
                    <SelectItem value="orthodontics">
                      Orthodontics/Braces
                    </SelectItem>
                    <SelectItem value="restoration">
                      General Restoration
                    </SelectItem>
                    <SelectItem value="cosmetic">Cosmetic Dentistry</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.dentalProcedureType && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.dentalProcedureType}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="smoking"
                  className="text-sm font-medium text-gray-700"
                >
                  Are you a smoker? If yes, how much a day? *
                </Label>
                <Select
                  value={formData.smokingHabits}
                  onValueChange={(value) =>
                    handleInputChange("smokingHabits", value)
                  }
                >
                  <SelectTrigger
                    className={`mt-1 h-12 text-base ${errors.smokingHabits ? "border-red-500" : ""}`}
                  >
                    <SelectValue placeholder="Select your smoking habits" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes, 1-5 cigarettes a day">Yes, 1-5 cigarettes a day</SelectItem>
                    <SelectItem value="Yes, 5-10 cigarettes a day">Yes, 5-10 cigarettes a day</SelectItem>
                    <SelectItem value="Yes, over 10 a day">Yes, over 10 a day</SelectItem>
                  </SelectContent>
                </Select>
                {errors.smokingHabits && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.smokingHabits}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="discomfort"
                  className="text-sm font-medium text-gray-700"
                >
                  Do you experience any kind of dental discomfort?
                </Label>
                <Textarea
                  id="discomfort"
                  value={formData.dentalDiscomfort}
                  onChange={(e) =>
                    handleInputChange("dentalDiscomfort", e.target.value)
                  }
                  placeholder="Describe any pain, sensitivity, or discomfort or write 'None'"
                  className="mt-1 min-h-[80px] text-base"
                />
              </div>

              <ResponsiveFormGroup columns={1}>
                <div>
                  <Label
                    htmlFor="mobility"
                    className="text-sm font-medium text-gray-700"
                  >
                    Do you have any mobility issues?
                  </Label>
                  <Input
                    id="mobility"
                    value={formData.mobilityIssues}
                    onChange={(e) =>
                      handleInputChange("mobilityIssues", e.target.value)
                    }
                    placeholder="List any mobility concerns or write 'None'"
                    className="mt-1 h-12 text-base"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="dietary"
                    className="text-sm font-medium text-gray-700"
                  >
                    Do you have any dietary restrictions?
                  </Label>
                  <Input
                    id="dietary"
                    value={formData.dietaryRestrictions}
                    onChange={(e) =>
                      handleInputChange("dietaryRestrictions", e.target.value)
                    }
                    placeholder="List dietary restrictions or write 'None'"
                    className="mt-1 h-12 text-base"
                  />
                </div>
              </ResponsiveFormGroup>
            </div>

            {/* Travel Information Section */}
            <div className="border border-gray-200 rounded-xl p-4 sm:p-6 space-y-4 bg-white shadow-sm">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
                <Plane className="w-4 h-4" />
                Your Travel to Medellín, Colombia
              </h3>
              <div className="rounded-lg bg-green-50 p-4 text-sm text-gray-700">
                <p>
                  JEMA takes care of every detail of your trip so you feel safe
                  and supported. By sharing this information, we can ensure your
                  journey is comfortable and worry-free.
                </p>
              </div>

              <div>
                <Label
                  htmlFor="travelConcerns"
                  className="text-sm font-medium text-gray-700"
                >
                  Do you have any concerns about traveling or staying in
                  Colombia?
                </Label>
                <Textarea
                  id="travelConcerns"
                  value={formData.travelConcerns}
                  onChange={(e) =>
                    handleInputChange("travelConcerns", e.target.value)
                  }
                  placeholder="Share any concerns about travel, safety, language, etc. or write 'None'"
                  className="mt-1 min-h-[80px] text-base"
                />
              </div>

              <div>
                <Label
                  htmlFor="travelAlone"
                  className="text-sm font-medium text-gray-700"
                >
                  Will you travel alone?
                </Label>
                <Select
                  value={formData.travelAlone}
                  onValueChange={(value) =>
                    handleInputChange("travelAlone", value)
                  }
                >
                  <SelectTrigger className="mt-1 h-12 text-base">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes, I'll travel alone</SelectItem>
                    <SelectItem value="No, with partner">
                      No, I'll travel with my partner
                    </SelectItem>
                    <SelectItem value="No, with family">
                      No, I'll travel with family
                    </SelectItem>
                    <SelectItem value="No, with friends">
                      No, I'll travel with friends
                    </SelectItem>
                    <SelectItem value="No">
                      No, but travel companion isn't decided yet
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="budget"
                  className="text-sm font-medium text-gray-700"
                >
                  What are your budget expectations?
                </Label>
                <Select
                  value={formData.budgetExpectations}
                  onValueChange={(value) =>
                    handleInputChange("budgetExpectations", value)
                  }
                >
                  <SelectTrigger className="mt-1 h-12 text-base">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-2500">Under $2,500</SelectItem>
                    <SelectItem value="2500-5000">$2,500 - $5,000</SelectItem>
                    <SelectItem value="5000-10000">$5,000 - $10,000</SelectItem>
                    <SelectItem value="10000-20000">
                      $10,000 - $20,000
                    </SelectItem>
                    <SelectItem value="over-20000">Over $20,000</SelectItem>
                    <SelectItem value="flexible">
                      Budget flexible, quality priority
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="interest"
                  className="text-sm font-medium text-gray-700"
                >
                  Personal interest in the country
                </Label>
                <Textarea
                  id="interest"
                  value={formData.personalInterest}
                  onChange={(e) =>
                    handleInputChange("personalInterest", e.target.value)
                  }
                  placeholder="What interests you about Colombia? (culture, food, sightseeing, etc.)"
                  className="mt-1 min-h-[80px] text-base"
                />
              </div>
            </div>

            {/* Continue Button */}
            <Button
              type="button"
              onClick={handleContinue}
              className="w-full h-12 text-base font-semibold hover:shadow-lg transition-all duration-200"
              size="lg"
              variant="jema"
            >
              Some more questions
            </Button>
          </form>
        </ResponsiveContainer>
      </div>

      {/* Desktop/Tablet: Modern full-screen layout */}
      <div className="hidden lg:block max-w-6xl mx-auto">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900">
              Medical & Travel Information
            </h2>
            <p className="text-lg text-gray-600">
              Help us prepare the best treatment plan for you
            </p>
          </div>

          <form
            ref={formRef}
            className="space-y-10"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleContinue();
            }}
          >
            {/* Basic Information Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#166B93]/10 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#166B93]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Basic Information
                </h3>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <div>
                  <Label
                    htmlFor="dateOfBirth"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Date of Birth *
                  </Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) =>
                      handleInputChange("dateOfBirth", e.target.value)
                    }
                    max={todayStr}
                    className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.dateOfBirth ? "border-red-500" : "border-gray-200"}`}
                  />
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="city"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    City *
                  </Label>
                  <Input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="Your city"
                    className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.city ? "border-red-500" : "border-gray-200"}`}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-sm mt-2">{errors.city}</p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="state"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    State/Province
                  </Label>
                  <Input
                    id="state"
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    placeholder="State/Province"
                    className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="country"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Country *
                  </Label>
                  <Input
                    id="country"
                    type="text"
                    value={formData.country}
                    onChange={(e) =>
                      handleInputChange("country", e.target.value)
                    }
                    placeholder="Your country"
                    className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.country ? "border-red-500" : "border-gray-200"}`}
                  />
                  {errors.country && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.country}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Medical History Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Medical History
                </h3>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="preExisting"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Do you have any pre-existing medical conditions?
                  </Label>
                  <Textarea
                    id="preExisting"
                    value={formData.preExistingConditions}
                    onChange={(e) =>
                      handleInputChange("preExistingConditions", e.target.value)
                    }
                    placeholder="List any medical conditions or write 'None'"
                    className="min-h-[120px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="medications"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Are you currently taking any medications?
                  </Label>
                  <Textarea
                    id="medications"
                    value={formData.currentMedications}
                    onChange={(e) =>
                      handleInputChange("currentMedications", e.target.value)
                    }
                    placeholder="List medications and dosages or write 'None'"
                    className="min-h-[120px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="allergies"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Do you have any known allergies?
                  </Label>
                  <Textarea
                    id="allergies"
                    value={formData.allergies}
                    onChange={(e) =>
                      handleInputChange("allergies", e.target.value)
                    }
                    placeholder="List any allergies or write 'None'"
                    className="min-h-[120px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="surgeries"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Major surgeries or medical procedures?
                  </Label>
                  <Textarea
                    id="surgeries"
                    value={formData.majorSurgeries}
                    onChange={(e) =>
                      handleInputChange("majorSurgeries", e.target.value)
                    }
                    placeholder="List any major surgeries or procedures or write 'None'"
                    className="min-h-[120px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
              </div>
            </div>

            {/* Dental Information Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-lg">🦷</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Dental Information
                </h3>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div>
                    <Label
                      htmlFor="procedureType"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      What type of dental procedure are you seeking? *
                    </Label>
                    <Select
                      value={formData.dentalProcedureType}
                      onValueChange={(value) =>
                        handleInputChange("dentalProcedureType", value)
                      }
                    >
                      <SelectTrigger
                        className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.dentalProcedureType ? "border-red-500" : "border-gray-200"}`}
                      >
                        <SelectValue placeholder="Select procedure type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="implants">
                          Dental Implants
                        </SelectItem>
                        <SelectItem value="veneers">Veneers</SelectItem>
                        <SelectItem value="crowns">Crowns</SelectItem>
                        <SelectItem value="whitening">
                          Teeth Whitening
                        </SelectItem>
                        <SelectItem value="orthodontics">
                          Orthodontics/Braces
                        </SelectItem>
                        <SelectItem value="restoration">
                          General Restoration
                        </SelectItem>
                        <SelectItem value="cosmetic">
                          Cosmetic Dentistry
                        </SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.dentalProcedureType && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.dentalProcedureType}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="smoking"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      Are you a smoker? If yes, how much a day? *
                    </Label>
                    <Select
                      value={formData.smokingHabits}
                      onValueChange={(value) =>
                        handleInputChange("smokingHabits", value)
                      }
                    >
                      <SelectTrigger
                        className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.smokingHabits ? "border-red-500" : "border-gray-200"}`}
                      >
                        <SelectValue placeholder="Select your smoking habits" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes, 1-5 cigarettes a day">Yes, 1-5 cigarettes a day</SelectItem>
                        <SelectItem value="Yes, 5-10 cigarettes a day">Yes, 5-10 cigarettes a day</SelectItem>
                        <SelectItem value="Yes, over 10 a day">Yes, over 10 a day</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.smokingHabits && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.smokingHabits}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label
                    htmlFor="discomfort"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Do you experience any kind of dental discomfort?
                  </Label>
                  <Textarea
                    id="discomfort"
                    value={formData.dentalDiscomfort}
                    onChange={(e) =>
                      handleInputChange("dentalDiscomfort", e.target.value)
                    }
                    placeholder="Describe any pain, sensitivity, or discomfort or write 'None'"
                    className="min-h-[100px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div>
                    <Label
                      htmlFor="mobility"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      Do you have any mobility issues?
                    </Label>
                    <Input
                      id="mobility"
                      value={formData.mobilityIssues}
                      onChange={(e) =>
                        handleInputChange("mobilityIssues", e.target.value)
                      }
                      placeholder="List any mobility concerns or write 'None'"
                      className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="dietary"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      Do you have any dietary restrictions?
                    </Label>
                    <Input
                      id="dietary"
                      value={formData.dietaryRestrictions}
                      onChange={(e) =>
                        handleInputChange("dietaryRestrictions", e.target.value)
                      }
                      placeholder="List dietary restrictions or write 'None'"
                      className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Travel Information Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#FF9142]/10 rounded-full flex items-center justify-center">
                  <Plane className="w-5 h-5 text-[#FF9142]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Your Travel to Medellín, Colombia
                </h3>
              </div>
              <div className="rounded-lg bg-green-50 p-4 text-sm text-gray-700">
                <p>
                  JEMA takes care of every detail of your trip so you feel safe
                  and supported. By sharing this information, we can ensure your
                  journey is comfortable and worry-free.
                </p>
              </div>

              <div className="space-y-6 mt-4">
                <div>
                  <Label
                    htmlFor="travelConcerns"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Do you have any concerns about traveling or staying in
                    Colombia?
                  </Label>
                  <Textarea
                    id="travelConcerns"
                    value={formData.travelConcerns}
                    onChange={(e) =>
                      handleInputChange("travelConcerns", e.target.value)
                    }
                    placeholder="Share any concerns about travel, safety, language, etc. or write 'None'"
                    className="min-h-[100px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div>
                    <Label
                      htmlFor="travelAlone"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      Will you travel alone?
                    </Label>
                    <Select
                      value={formData.travelAlone}
                      onValueChange={(value) =>
                        handleInputChange("travelAlone", value)
                      }
                    >
                      <SelectTrigger className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">
                          Yes, I'll travel alone
                        </SelectItem>
                        <SelectItem value="No, with partner">
                          No, I'll travel with my partner
                        </SelectItem>
                        <SelectItem value="No, with family">
                          No, I'll travel with family
                        </SelectItem>
                        <SelectItem value="No, with friends">
                          No, I'll travel with friends
                        </SelectItem>
                        <SelectItem value="No">
                          No, but travel companion isn't decided yet
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label
                      htmlFor="budget"
                      className="text-base font-medium text-gray-700 mb-2 block"
                    >
                      What are your budget expectations?
                    </Label>
                    <Select
                      value={formData.budgetExpectations}
                      onValueChange={(value) =>
                        handleInputChange("budgetExpectations", value)
                      }
                    >
                      <SelectTrigger className="h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-2500">Under $2,500</SelectItem>
                        <SelectItem value="2500-5000">
                          $2,500 - $5,000
                        </SelectItem>
                        <SelectItem value="5000-10000">
                          $5,000 - $10,000
                        </SelectItem>
                        <SelectItem value="10000-20000">
                          $10,000 - $20,000
                        </SelectItem>
                        <SelectItem value="over-20000">Over $20,000</SelectItem>
                        <SelectItem value="flexible">
                          Budget flexible, quality priority
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label
                    htmlFor="interest"
                    className="text-base font-medium text-gray-700 mb-2 block"
                  >
                    Personal interest in the country
                  </Label>
                  <Textarea
                    id="interest"
                    value={formData.personalInterest}
                    onChange={(e) =>
                      handleInputChange("personalInterest", e.target.value)
                    }
                    placeholder="What interests you about Colombia? (culture, food, sightseeing, etc.)"
                    className="min-h-[100px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
              </div>
            </div>
          </form>

          {/* Continue Button */}
          <div className="flex justify-center pt-8">
            <Button
              onClick={handleContinue}
              className="w-full max-w-sm mx-auto h-14 text-lg font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              size="lg"
              variant="jema"
            >
              Some more questions
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
